import { useCallback, useRef, useState } from "react";
import { Flex, Modal, ModalBody, ModalHeader, ModalOverlay, UseDisclosureReturn, Text } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { useGenericActivityFromElysiumMutation } from "src/api/client/generic-activity-from-elysium.api";
import { EnumActivityType } from "src/api/client/generic-activity.api";
import { useNewsTradersReactionsQuery } from "src/api/client/news/news-traders-reactions.api";
import { WfModalCloseButton } from "src/components/base/wf-modal-close-button";
import { EmptyState } from "src/components/common/empty-state";
import { TUserReactions } from "src/components/user-reactions/types";
import { EnumToastStatus, useWfToast } from "src/hooks/use-wf-toast";
import { DropDownSelector } from "./drop-down-selector";
import { LoadingSkeleton } from "./loading-skeleton";
import { ModalContentWrapper } from "./modal-content-wrapper";
import { EnumReactionArrowIndicator } from "./reaction-arrow-indicator";
import { TradersWhoReacted } from "./traders-who-reacted";

interface IFeedReportModalProps {
    modalProps: UseDisclosureReturn;
    slug: string;
    reactionsWithUnderlyingIsin: TUserReactions;
}

export const AllTraderReactionsModal = (props: IFeedReportModalProps) => {
    const genericActivity = useGenericActivityFromElysiumMutation();
    const { modalProps, slug, reactionsWithUnderlyingIsin } = props;

    const seenIsins = useRef(new Set(null));

    const reactions = reactionsWithUnderlyingIsin.map(({ underlyingIsin, underlyingName }) => ({
        underlyingIsin,
        underlyingName,
    }));

    const { t } = useTranslation(["news", "common"]);
    const [firstReaction] = reactionsWithUnderlyingIsin;

    const [selectedUnderlyingIsin, setSelectedUnderlyingIsin] = useState(firstReaction.underlyingIsin!);
    const toast = useWfToast();

    const trackTraderReactionsUnderlyingSeen = useCallback(
        (isin: string) =>
            genericActivity({
                activityTypeId: EnumActivityType.TopTraderReactionsUnderlyingSeen,
                parameter: JSON.stringify({ isin }),
                referrer: window.location.href,
            }),
        [genericActivity]
    );

    const { isLoading, data } = useNewsTradersReactionsQuery({
        underlyingIsin: selectedUnderlyingIsin,
        slug,
        options: {
            enabled: modalProps.isOpen,
            onError: () => {
                toast({ status: EnumToastStatus.Error, title: t("general-error", { ns: "common" }) });
                modalProps.onClose();
            },
        },
    });

    const onClose = () => {
        modalProps.onClose();
    };

    const onChange = (underlyingIsin: string) => {
        if (underlyingIsin !== selectedUnderlyingIsin) {
            setSelectedUnderlyingIsin(underlyingIsin);
        }
    };

    const hasNoResults = !data || Object.values(data).every(traders => traders.length === 0);

    if (modalProps.isOpen && selectedUnderlyingIsin && !seenIsins.current.has(selectedUnderlyingIsin)) {
        trackTraderReactionsUnderlyingSeen(selectedUnderlyingIsin);
        seenIsins.current.add(selectedUnderlyingIsin);
    }

    return (
        <Modal blockScrollOnMount={true} scrollBehavior="inside" {...modalProps} isOpen={modalProps.isOpen}>
            <ModalOverlay />
            <ModalContentWrapper>
                <ModalHeader pt={[2, 5]}>
                    <Flex justifyContent="space-between" alignItems="center" flexWrap={["wrap", "initial"]} gap={2}>
                        <Text>{t("modal.trader-reactions-too")}</Text>
                        <DropDownSelector
                            order={[3, "unset"]}
                            onChange={onChange}
                            selectedUnderlyingIsin={selectedUnderlyingIsin}
                            reactions={reactions}
                        />
                        <WfModalCloseButton
                            order={[2, "unset"]}
                            ml="auto"
                            alignSelf={["self-start", "initial"]}
                            position="inherit"
                            onClick={onClose}
                        />
                    </Flex>
                </ModalHeader>

                <ModalBody pb={5} minH={["408px", "300px"]}>
                    {isLoading && <LoadingSkeleton />}
                    {!isLoading && data && (
                        <>
                            {data.bullish.length > 0 && (
                                <TradersWhoReacted
                                    key={`${selectedUnderlyingIsin}-bullish`}
                                    traders={data.bullish}
                                    variantReactionArrowIndicator={EnumReactionArrowIndicator.BULLISH}
                                    pb={1}
                                />
                            )}
                            {data.bearish.length > 0 && (
                                <TradersWhoReacted
                                    key={`${selectedUnderlyingIsin}-bearish`}
                                    traders={data.bearish}
                                    variantReactionArrowIndicator={EnumReactionArrowIndicator.BEARISH}
                                    pb={1}
                                />
                            )}

                            {data.neutral.length > 0 && (
                                <TradersWhoReacted
                                    key={`${selectedUnderlyingIsin}-neutral`}
                                    traders={data.neutral}
                                    variantReactionArrowIndicator={EnumReactionArrowIndicator.NEUTRAL}
                                    pb={1}
                                />
                            )}
                        </>
                    )}

                    {!isLoading && hasNoResults && (
                        <EmptyState
                            pt={[2, 3]}
                            p={0}
                            pb={0}
                            boxShadow="none"
                            title={t("modal.no-trader-reaction-title")}
                            description={t("modal.no-trader-reaction-subtitle")}
                        />
                    )}
                </ModalBody>
            </ModalContentWrapper>
        </Modal>
    );
};
